import Widget from 'components/Widget';
import React, { useContext } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { IoTrash } from 'react-icons/io5';
import AddWidgetComponent from '../../components/AddWidgetComponent';
import './assets/Dashboard.css';

import { LayoutSizeContext } from 'App';
import { Button } from 'react-bootstrap';

const ResponsiveGridLayout = WidthProvider(Responsive);

export default function DashboardPreview({
  definedWidgetData,
  list,
  setList,
  onRemoveWidget,
  openWidgetSelector,
  uninteractable,
  edit = true,
  compactPreview = false,
}) {
  const layoutSize = useContext(LayoutSizeContext);
  const gridCols = compactPreview
    ? { xs: 1, sm: 1, md: 3, lg: 4, xl: 6, xxl: 6 }
    : { xs: 1, sm: 1, md: 1, lg: 2, xl: 3, xxl: 3 };
  const gridMargin = compactPreview ? [4, 4] : [10, 10];
  const gridContainerPadding = compactPreview ? [0, 0] : undefined;
  const previewRowHeight = compactPreview
    ? Math.max(95, Math.round(layoutSize.widgetHeight * 0.41))
    : layoutSize.widgetHeight;
  const previewAreaStyle = compactPreview
    ? {
        height: '100%',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        boxSizing: 'border-box',
        paddingBottom: edit && !uninteractable ? 64 : 8,
      }
    : {
        maxHeight: '65vh',
        overflowX: 'hidden',
        overflowY: 'auto',
        boxSizing: 'border-box',
      };

  const onListChange = (newList) => {
    setList(newList);
  };

  const appendAddWidget = (arr) => [
    ...arr,
    {
      i: 'add',
      displayed: true,
      isDraggable: false,
      locked: true,
      static: false,
      x: 0,
      y: Infinity,
      w: 1,
      h: 1,
    },
  ];

  const createLayout = (list, uninteractable) => ({
    xxl: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: item.w,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
    xl: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: item.w,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
    lg: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: item.w,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
    md: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: 1,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
    sm: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: 1,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
    xs: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: 1,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
  });

  const layouts = createLayout(list ?? [], uninteractable);

  return (
    <div className="w-100 h-100 d-flex flex-grow-full flex-fill">
      <ResponsiveGridLayout
        breakpoints={{ xs: 0, sm: 576, md: 768, lg: 992, xl: 1200, xxl: 1400 }}
        cols={gridCols}
        rowHeight={previewRowHeight}
        margin={gridMargin}
        containerPadding={gridContainerPadding}
        compactType="vertical"
        layouts={layouts}
        onDragStop={(layout) => {
          const temp = [...list];
          for (let i = 0; i < layout.length; i++) {
            const id = layout[i].i;
            if (id === 'add') continue;
            const idx = parseInt(id, 10);
            if (!isNaN(idx) && temp[idx])
              temp[idx] = { ...temp[idx], x: layout[i].x, y: layout[i].y };
          }

          onListChange(temp);
        }}
        draggableHandle=".handle"
        className="align-items-start w-100 h-100"
        style={previewAreaStyle}
      >
        {list &&
          list.map((item, index) => {
            if (item?.displayed === false || item?.active === false) return null;

            return (
              <div key={String(index)}>
                <Widget
                  key={String(index)}
                  item={{ ...definedWidgetData[item.id], ...item }}
                  index={String(index)}
                  expandable
                  list={list}
                  onListChange={onListChange}
                  uninteractable={uninteractable}
                  dashboardPreview={true}
                  scale={1}
                />

                {onRemoveWidget && edit && (
                  <Button
                    className="position-absolute d-flex justify-content-center align-items-center p-0 rounded-circle bg-danger"
                    style={{ top: 13, right: 65, width: 30, height: 30 }}
                    onClick={() => onRemoveWidget(index)}
                  >
                    <IoTrash color="white" size={19} />
                  </Button>
                )}
              </div>
            );
          })}

        {edit && (
          <div
            key="add"
            className="d-flex align-items-center justify-content-center rounded-2 overflow-hidden bg-base"
          >
            <AddWidgetComponent openWidgetSelector={() => openWidgetSelector(true)} />
          </div>
        )}
      </ResponsiveGridLayout>
    </div>
  );
}
